import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@mui/material';
import ErrorHandlingLayout from 'app/shared/componentsRestyled/common/ErrorHandlingLayout';
import {
  AutocompleteCDS,
  onAutocompleteChangeInForm,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import { TableCDS } from 'app/shared/componentsRestyled/coreDesignSystem/Table';
import dayjs from 'dayjs';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useErrorState from 'hooks/useErrorState';
import useForm from 'hooks/useForm';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';

import {
  copyDocumentsToTargetLoanAction,
  resetSelectedDocumentsAction,
} from 'store/documents/actions';
import { getSelectedDocumentsData, getSelectedDocumentsIds } from 'store/documents/selectors';
import {
  closeDialogAction,
  showMessageAction,
  updateDialogOptionsAction,
} from 'store/global/actions';
import { getRelatedLoansAction } from 'store/loanApplications/actions';
import { selectRelatedLoans } from 'store/loanApplications/selectors';

import { schema, tableConfig } from './CopyDocumentsForm.config';
import { CopyDocumentsFormTestIds } from './CopyDocumentsForm.testIds';

const getOptionLabel = option => {
  if (!option) {
    return '';
  }

  const { loanNumber, subjectPropertyAddress, createdDate } = option;

  return `#${loanNumber} (${subjectPropertyAddress}, created on ${dayjs(createdDate).format(
    COMMON_DATE_FORMAT,
  )})`;
};

const CopyDocumentsForm = ({ loanId, applicationId, dialogId, isLoading, setIsLoading }) => {
  const dispatch = useDispatch();

  const relatedLoans = useSelector(selectRelatedLoans);
  const selectedDocuments = useSelector(getSelectedDocumentsData);
  const selectedDocumentsIds = useSelector(getSelectedDocumentsIds);

  const { isError, resetError, setError } = useErrorState();
  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();
  const [isSaving, setIsSaving] = useState(false);

  const {
    setInForm,
    validationErrors,
    validateField,
    validateForm,
    setSchema,
    isFormChanged,
    form,
    formRef,
  } = useForm({
    targetLoan: null,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const fetchRelatedLoans = useCallback(() => {
    setIsLoading(true);
    resetError();

    dispatch(getRelatedLoansAction({ applicationId }))
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [dispatch, applicationId, setIsLoading, resetError, setError]);

  const afterCloseCallback = useCallback(
    () => dispatch(resetSelectedDocumentsAction()),
    [dispatch],
  );

  const onSubmit = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }

    const targetLoan = formRef.current?.targetLoan;
    const selectedDocumentsLength = selectedDocumentsIds.length;

    const params = {
      sourceLoanId: loanId,
      targetLoanId: targetLoan?.loanId,
      documentIds: selectedDocumentsIds,
    };

    setIsSaving(true);
    setIsLoading(true);

    dispatch(copyDocumentsToTargetLoanAction(params))
      .then(() => {
        dispatch(
          showMessageAction({
            variant: 'success',
            message: `${selectedDocumentsLength} ${
              selectedDocumentsLength === 1 ? 'document' : 'documents'
            } successfully copied to Loan ${getOptionLabel(targetLoan)}`,
          }),
        );
        dispatch(closeDialogAction(dialogId));
        afterCloseCallback();
      })
      .catch(Error)
      .finally(() => {
        setIsSaving(false);
        setIsLoading(false);
      });
  }, [
    dispatch,
    formRef,
    selectedDocumentsIds,
    loanId,
    validateForm,
    setIsLoading,
    setIsSaving,
    afterCloseCallback,
    dialogId,
  ]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffectSafeStrictMode(() => {
    if (relatedLoans.length) {
      return;
    }

    fetchRelatedLoans();
  }, [fetchRelatedLoans, relatedLoans.length]);

  useEffect(() => {
    dispatch(
      updateDialogOptionsAction(dialogId, {
        isAcceptButtonDisabled: isLoading || !isFormChanged,
        onAcceptClick: onSubmit,
        afterCloseCallback,
      }),
    );
  }, [dispatch, isLoading, dialogId, isFormChanged, onSubmit, afterCloseCallback]);

  return (
    <ErrorHandlingLayout isError={isError} onClick={fetchRelatedLoans}>
      <TruncatedProvider />
      <Box
        className="h-full flex flex-col gap-24"
        onMouseLeave={onResetTooltipState}
        onMouseOver={onMouseOver}
      >
        <AutocompleteCDS
          required
          withSortedOptions
          dataTestId={CopyDocumentsFormTestIds.TARGET_LOAN_SELECT}
          disabled={isLoading}
          error={!!validationErrors.targetLoan}
          getOptionLabel={getOptionLabel}
          helperText={validationErrors.targetLoan}
          label="Target Loan"
          name="targetLoan"
          optionKey="loanId"
          options={relatedLoans}
          value={form.targetLoan}
          onChange={onAutocompleteChange}
        />

        <Box className="flex-1 max-h-400 overflow-hidden">
          <TableCDS
            isStickyHeader
            className="h-full"
            config={tableConfig}
            data={selectedDocuments}
            isLoading={(isLoading && !isSaving) || isError}
            skeletonRows={2}
          />
        </Box>
      </Box>
    </ErrorHandlingLayout>
  );
};

CopyDocumentsForm.propTypes = {
  ...WithLoadingPropTypes,
  dialogId: PropTypes.string.isRequired,
  applicationId: PropTypes.string.isRequired,
  loanId: PropTypes.string.isRequired,
};

export default withLoading(CopyDocumentsForm);
